import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import LogoutButton from "../components/LogoutButton";
import {
  Loader2,
  Users,
  Sun,
  Moon,
  CalendarDays,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { serverTimestamp, updateDoc } from "firebase/firestore";



/* ================= BRANCH LIST ================= */
const BRANCHES = [
  "AGONA","BAKAEKYIR","ELUBO","AXIM","ESIAMA",
  "VOL-MAGT","TYPE-C","KOJOKROM","ANAJI-K","MPOHOR",
];
const formatGhs = (value) =>
  Number(value || 0).toFixed(2);

export default function GMDashboard() {
  const navigate = useNavigate();

  /* ================= STATE ================= */
  const [reports, setReports] = useState([]);
  const [activeBranch, setActiveBranch] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [theme, setTheme] = useState("light");
  const [useRange, setUseRange] = useState(false);
const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");
const [currentUser, setCurrentUser] = useState(null);
const [allUsers, setAllUsers] = useState([]);
const onlineCount = allUsers.filter(u => u.online).length;

const [showOnlineUsers, setShowOnlineUsers] = useState(false);
const [showProfile, setShowProfile] = useState(false);
const [profileForm, setProfileForm] = useState({
  name: "",
  phone: "",
});
const [savingProfile, setSavingProfile] = useState(false);





  // table filter only
  const [exactDate, setExactDate] = useState("");
    // pagination
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);


  /* ================= LOAD ================= */
  useEffect(() => {
    const load = async () => {
      const snap = await getDocs(collection(db, "reports"));
      setReports(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    load();
  }, []);

  useEffect(() => {
  const loadUsers = async () => {
    const snap = await getDocs(collection(db, "users"));
    setAllUsers(
      snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }))
    );
  };

  loadUsers();
}, []);


  useEffect(() => {
  const unsub = onAuthStateChanged(auth, async (u) => {
    if (!u) return;

    const userRef = doc(db, "users", u.uid);

    // mark online
    await updateDoc(userRef, {
      online: true,
      lastLogin: serverTimestamp(),
    });

    const snap = await getDoc(userRef);
    if (snap.exists()) {
      setCurrentUser({ uid: u.uid, ...snap.data() });
    }
  });

  // mark offline on close
  window.addEventListener("beforeunload", async () => {
    if (!auth.currentUser) return;
    await updateDoc(doc(db, "users", auth.currentUser.uid), {
      online: false,
      lastSeen: serverTimestamp(),
    });
  });

  return () => unsub();
}, []);

    /* ================= RESET PAGE ON FILTER CHANGE ================= */
useEffect(() => {
  setPage(1);
}, [activeBranch, exactDate, fromDate, toDate, useRange]);



  const deleteReport = async (id) => {
  const confirm = window.confirm(
    "Are you sure you want to delete this transaction?"
  );

  if (!confirm) return;

  try {
    setLoadingId(id);
    await deleteDoc(doc(db, "reports", id));
    setReports(prev => prev.filter(r => r.id !== id));
  } catch (err) {
    console.error(err);
    alert("Failed to delete transaction");
  } finally {
    setLoadingId(null);
  }
};


  /* ================= DATE HELPERS ================= */
  const isToday = useCallback((ts) => {
    return ts?.toDate()?.toDateString() === new Date().toDateString();
  }, []);

const isThisMonth = useCallback((ts) => {
  const d = ts?.toDate();
  const now = new Date();
  return (
    d &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear()
  );
}, []);

const isExactDate = useCallback((ts) => {
  if (!exactDate) return true;
  const [y, m, d] = exactDate.split("-").map(Number);
  const r = ts?.toDate();
  return r &&
    r.getFullYear() === y &&
    r.getMonth() === m - 1 &&
    r.getDate() === d;
}, [exactDate]);

const isInRange = useCallback((ts) => {
  if (!fromDate || !toDate) return true;
  const d = ts?.toDate();
  if (!d) return false;

  const start = new Date(fromDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(toDate);
  end.setHours(23, 59, 59, 999);

  return d >= start && d <= end;
}, [fromDate, toDate]);

  /* ================= EXPORT CSV ================= */
  const exportToCSV = () => {
  if (!tableData.length) {
    alert("No data to export");
    return;
  }

  const headers = [
    "Customer",
    "Amount",
    "Commission",
    "Branch",
    "Date",
  ];

  const rows = tableData.map(r => [
    r.customerName,
    r.amount,
    formatGhs(r.commission),
    r.branchId,
    r.createdAt?.toDate()?.toLocaleDateString(),
  ]);

  const csvContent =
    "data:text/csv;charset=utf-8," +
    [headers, ...rows]
      .map(e => e.join(","))
      .join("\n");

  const link = document.createElement("a");
  link.href = encodeURI(csvContent);
  link.download = `${activeBranch}-transactions.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
/* ================= BRANCH FILTERED BASE ================= */
const branchBaseReports = useMemo(() => {
  if (!activeBranch) return [];
  return reports.filter(r => r.branchId === activeBranch);
}, [reports, activeBranch]);

const branchSelectedReports = useMemo(() => {
  if (!activeBranch) return [];

  let filtered;

  if (useRange) {
    filtered = branchBaseReports.filter(r => isInRange(r.createdAt));
  } else if (exactDate) {
    filtered = branchBaseReports.filter(r => isExactDate(r.createdAt));
  } else {
    filtered = branchBaseReports.filter(r => isToday(r.createdAt));
  }

  return filtered.sort((a, b) =>
    b.createdAt?.toDate?.() - a.createdAt?.toDate?.()
  );
}, [
  branchBaseReports,
  activeBranch,
  exactDate,
  useRange,
  isToday,
  isExactDate,
  isInRange
]);


const branchSelectedCommission = useMemo(
  () =>
    branchSelectedReports.reduce(
      (s, r) => s + Number(r.commission || 0),
      0
    ),
  [branchSelectedReports]
);

const branchSelectedCount = branchSelectedReports.length;

/* ================= MONTHLY ================= */
const branchMonthReports = useMemo(
  () =>
    branchBaseReports.filter(r =>
      isThisMonth(r.createdAt)
    ),
  [branchBaseReports, isThisMonth]
);

const branchMonthCommission = useMemo(
  () =>
    branchMonthReports.reduce(
      (s, r) => s + Number(r.commission || 0),
      0
    ),
  [branchMonthReports]
);

const branchMonthCount = branchMonthReports.length;

/* ================= ALL TIME ================= */
const branchAllCommission = useMemo(
  () =>
    branchBaseReports.reduce(
      (s, r) => s + Number(r.commission || 0),
      0
    ),
  [branchBaseReports]
);

const branchAllCount = branchBaseReports.length;



  /* ================= GLOBAL TOTALS ================= */
 const totalToday = useMemo(
  () =>
    reports
      .filter(r => isToday(r.createdAt))
      .reduce((s, r) => s + Number(r.commission || 0), 0),
  [reports, isToday]
);

const totalMonth = useMemo(
  () =>
    reports
      .filter(r => isThisMonth(r.createdAt))
      .reduce((s, r) => s + Number(r.commission || 0), 0),
  [reports, isThisMonth]
);

/* ================= GLOBAL COUNTS ================= */
const todayCount = useMemo(
  () =>
    reports.filter(r => isToday(r.createdAt)).length,
  [reports, isToday]
);

const monthCount = useMemo(
  () =>
    reports.filter(r => isThisMonth(r.createdAt)).length,
  [reports, isThisMonth]
);

const tableData = branchSelectedReports;



/* ================= PAGINATION ================= */
const totalPages = Math.ceil(tableData.length / PAGE_SIZE);

const paginatedData = useMemo(() => {
  const start = (page - 1) * PAGE_SIZE;
  return tableData.slice(start, start + PAGE_SIZE);
}, [tableData, page]);




  /* ================= THEME ================= */
  const bg =
    theme === "dark"
      ? "bg-gradient-to-br from-[#020617] via-black to-[#0c4a6e] text-white"
      : "bg-gray-100 text-black";

  const card =
  theme === "dark"
    ? "bg-white/5 border border-white/40"
    : "bg-white border border-black/10";

  const btn =
    theme === "dark"
      ? "border border-white/10 hover:bg-white/5"
      : "border border-black/20 hover:bg-black/5";

  return (
    <div className={`min-h-screen ${bg}`}>
      <div className="p-4 max-w-7xl mx-auto">

        {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">
  Management Page
</h2>

{currentUser && (
  <p className="text-sm opacity-70 mt-1">
    Hi, <span className="font-semibold">{currentUser.name}</span>
  </p>
)}
{currentUser && (
  <p className="text-xs opacity-60">
    Logged in as:{" "}
    {currentUser.role === "gm"
      ? "Manager"
      : currentUser.role || "User"}
    {currentUser.branchId && ` • ${currentUser.branchId}`}
  </p>
)}


            <p className="opacity-70 text-sm">
              Daily & Monthly Tracker
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setTheme(t => (t === "light" ? "dark" : "light"))}
              className={`px-3 py-2 rounded-xl ${btn}`}
            >
              {theme === "light" ? <Moon /> : <Sun />}
            </button>

<button
  onClick={() => navigate("/admin/approvals")}
  className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium flex items-center gap-1"
>
  <Users size={14} />
  Approvals
</button>
<button
  onClick={() => navigate("/gm/customers")}
  className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium flex items-center gap-1"
>
  <Users size={14} />
  Customers
</button>
<button
  onClick={() => navigate("/gm/leaderboard")}
  className="px-3 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-medium"
>
  PAYG Leaderboard
</button>
<button
  onClick={() => navigate("/gm/ecw")}
  className="px-3 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-medium"
>
  ECW Report
</button>

            <LogoutButton />
     {currentUser && (
  <button
    onClick={() => {
      setProfileForm({
        name: currentUser.name || "",
        phone: currentUser.phone || "",
      });
      setShowProfile(true);
    }}
    className="
      flex items-center gap-3
      px-3 py-1.5
      rounded-full
      bg-white/70 dark:bg-white/5
      backdrop-blur-xl
      border border-black/5 dark:border-white/20
      shadow-sm
      hover:shadow-emerald-500/20
      transition
    "
  >
    {/* AVATAR */}
    <div className="relative">
      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white text-sm font-bold">
        {currentUser.name?.charAt(0)}
      </div>

      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-black" />
    </div>

    <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
      {currentUser.name?.split(" ")[0]}
    </span>
  </button>
)}


          </div>
        </div>
      
        <button
  onClick={() => setShowOnlineUsers(s => !s)}
  className={`relative px-3 py-1.5 rounded-lg text-sm font-medium
    ${btn}`}
>
  👥 Online Users

  {/* GREEN COUNT */}
  {onlineCount > 0 && (
    <span className="ml-2 inline-flex items-center gap-2 text-emerald-600 font-semibold">
      <span className="w-2 h-2 bg-emerald-500 rounded-full" />
      {onlineCount}
    </span>
  )}
</button>
{showProfile && currentUser && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
    <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-xl border dark:border-white/10">
      
      <h3 className="text-lg font-bold mb-4">
        Edit Profile
      </h3>

      {/* NAME */}
      <label className="block text-sm mb-1 opacity-70">
        Full Name
      </label>
      <input
        value={profileForm.name}
        onChange={(e) =>
          setProfileForm({ ...profileForm, name: e.target.value })
        }
        className="w-full mb-3 p-2 rounded-lg border dark:bg-black/40"
      />

      {/* PHONE */}
      <label className="block text-sm mb-1 opacity-70">
        Phone (optional)
      </label>
      <input
        value={profileForm.phone}
        onChange={(e) =>
          setProfileForm({ ...profileForm, phone: e.target.value })
        }
        className="w-full mb-4 p-2 rounded-lg border dark:bg-black/40"
      />

      {/* READ ONLY INFO */}
      <p className="text-xs opacity-60 mb-4">
        Role: {currentUser.role} <br />
        Branch: {currentUser.branchId || "N/A"}
      </p>

      {/* ACTIONS */}
      <div className="flex justify-end gap-2">
        <button
          onClick={() => setShowProfile(false)}
          className="px-4 py-2 rounded-lg border"
        >
          Cancel
        </button>

        <button
          disabled={savingProfile}
          onClick={async () => {
            try {
              setSavingProfile(true);
              await updateDoc(
                doc(db, "users", currentUser.uid),
                {
                  name: profileForm.name,
                  phone: profileForm.phone,
                }
              );
              setCurrentUser((u) => ({
                ...u,
                name: profileForm.name,
                phone: profileForm.phone,
              }));
              setShowProfile(false);
            } catch (err) {
              alert("Failed to update profile");
            } finally {
              setSavingProfile(false);
            }
          }}
          className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
        >
          {savingProfile ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  </div>
)}




      {showOnlineUsers && (
  <div className={`mb-6 p-4 rounded-2xl ${card}`}>
    <div className="flex justify-between items-center mb-3">
      <p className="text-sm font-semibold opacity-70">
        Logged-in Users
      </p>

      <button
        onClick={() => setShowOnlineUsers(false)}
        className="text-xs opacity-60 hover:opacity-100"
      >
        Close
      </button>
    </div>

   <div className="flex flex-wrap gap-3">
  {allUsers
    .filter(u => u.online) // 👈 ONLY ONLINE
    .map((u) => (
      <div
        key={u.id}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border"
      >
        {/* GREEN DOT */}
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />

        <span className="font-medium">
          {u.name}
        </span>

        <span className="text-xs opacity-60">
          {u.role || "User"}
        </span>
      </div>
    ))}
</div>

  </div>
)}



       {/* ================= GLOBAL DASHBOARD ================= */}
<div className="grid md:grid-cols-4 gap-4 mb-6">
  <div className={`p-5 rounded-2xl ${card} text-center`}>
    <p className="text-sm opacity-70">Today Commission</p>
    <p className="text-2xl font-bold">
      GHS {formatGhs(totalToday)}
    </p>
    <p className="text-xs opacity-60">
      {todayCount} transactions
    </p>
  </div>

  <div className={`p-5 rounded-2xl ${card} text-center`}>
    <p className="text-sm opacity-70">Today Count</p>
    <p className="text-2xl font-bold">{todayCount}</p>
    <p className="text-xs opacity-60">transactions</p>
  </div>

  <div className={`p-5 rounded-2xl ${card} text-center`}>
    <p className="text-sm opacity-70">Monthly Commission</p>
    <p className="text-2xl font-bold">
      GHS {formatGhs(totalMonth)}
    </p>
    <p className="text-xs opacity-60">
      {monthCount} transactions
    </p>
  </div>

  <div className={`p-5 rounded-2xl ${card} text-center`}>
    <p className="text-sm opacity-70">Monthly Count</p>
    <p className="text-2xl font-bold">{monthCount}</p>
    <p className="text-xs opacity-60">transactions</p>
  </div>
</div>

{/* ================= BRANCH SELECT ================= */}
<div className="flex flex-wrap gap-2 mb-4">
  {BRANCHES.map((b) => (
    <button
      key={b}
      onClick={() => setActiveBranch(b)}
      className={`px-4 py-2 rounded-full text-sm font-semibold
        ${activeBranch === b ? "bg-sky-600 text-white" : btn}`}
    >
      {b}
    </button>
  ))}
</div>

{/* ================= DATE FILTERS ================= */}
{activeBranch && (
  <div className="flex flex-col gap-3 mb-5">

    {/* RANGE TOGGLE */}
    <label className="flex items-center gap-2 text-sm font-medium">
      <input
        type="checkbox"
        checked={useRange}
        onChange={(e) => {
          setUseRange(e.target.checked);
          setExactDate("");
        }}
      />
      Use Date Range
    </label>

    {/* RANGE PICKER */}
    {useRange ? (
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className={`p-2 rounded-lg border
            ${theme === "dark"
              ? "bg-black/40 text-white border-white/40 [color-scheme:dark]"
              : "bg-white text-black border-black/30"}`}
        />

        <span className="text-sm opacity-70">to</span>

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className={`p-2 rounded-lg border
            ${theme === "dark"
              ? "bg-black/40 text-white border-white/40 [color-scheme:dark]"
              : "bg-white text-black border-black/30"}`}
        />
      </div>
    ) : (
      <div className="flex items-center gap-2">
        <CalendarDays size={16} />
        <input
          type="date"
          value={exactDate}
          onChange={(e) => setExactDate(e.target.value)}
          className={`p-2 rounded-lg border
            ${theme === "dark"
              ? "bg-black/40 text-white border-white/40 [color-scheme:dark]"
              : "bg-white text-black border-black/30"}`}
        />
        <span className="text-sm opacity-70">
          Filter exact date
        </span>
      </div>
    )}
  </div>
)}

{/* ================= BRANCH DASHBOARD ================= */}
{activeBranch && (
  <div className="grid md:grid-cols-3 gap-4 mb-6">

    <div className={`p-5 rounded-2xl ${card} text-center`}>
      <p className="text-sm opacity-70">
        {useRange && fromDate && toDate
          ? `${activeBranch} (${fromDate} → ${toDate})`
          : exactDate
          ? `${activeBranch} (${exactDate})`
          : `${activeBranch} Today`}
      </p>

      <p className="text-2xl font-bold">
        GHS {formatGhs(branchSelectedCommission)}
      </p>

      <p className="text-xs opacity-60">
        {branchSelectedCount} transactions
      </p>
    </div>

    <div className={`p-5 rounded-2xl ${card} text-center`}>
      <p className="text-sm opacity-70">
        {activeBranch} This Month
      </p>

      <p className="text-2xl font-bold">
        GHS {formatGhs(branchMonthCommission)}
      </p>

      <p className="text-xs opacity-60">
        {branchMonthCount} transactions
      </p>
    </div>

    <div className={`p-5 rounded-2xl ${card} text-center`}>
      <p className="text-sm opacity-70">
        {activeBranch} All Time
      </p>

      <p className="text-2xl font-bold">
        GHS {formatGhs(branchAllCommission)}
      </p>

      <p className="text-xs opacity-60">
        {branchAllCount} transactions
      </p>
    </div>

  </div>
)}

{/* ================= REPORT TABLE ================= */}
<div className="mb-20">
  <div className="flex justify-between items-center mb-4">
    <h3 className="text-xl font-bold">
      {activeBranch
        ? `Transactions for ${activeBranch}`
        : "Select a branch to view transactions"}
    </h3>
    {activeBranch && (
      <button
        onClick={exportToCSV}
        className={`px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white ${btn}`}
      >
        Export CSV
      </button>
    )}
  </div>
</div>


       {/* TABLE */}
{activeBranch && (
  <>
    <div className={`overflow-x-auto rounded-2xl ${card}`}>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="p-3 text-left">Customer</th>
            <th className="p-3 text-left">Amount</th>
            <th className="p-3 text-left">Commission</th>
            <th className="p-3 text-left">Date</th>
            <th className="p-3 text-left">Action</th>
          </tr>
        </thead>

        <tbody>
          {paginatedData.map((r) => (
            <tr key={r.id} className="border-t">
              <td className="p-3 font-semibold">
                {r.customerName}
              </td>

              <td className="p-3">
                GHS {r.amount}
              </td>

              <td className="p-3 font-bold">
                GHS {formatGhs(r.commission)}
              </td>

              <td className="p-3">
                {r.createdAt?.toDate()?.toLocaleDateString()}
              </td>

              <td className="p-3">
                <button
                  disabled={loadingId === r.id}
                  onClick={() => deleteReport(r.id)}
                  className="px-3 py-1 rounded-full bg-red-600 hover:bg-red-500 text-white text-xs disabled:opacity-50"
                >
                  {loadingId === r.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    "Delete"
                  )}
                </button>
              </td>
            </tr>
          ))}

          {paginatedData.length === 0 && (
            <tr>
              <td
                colSpan="5"
                className="p-6 text-center opacity-70"
              >
                No records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    {/* PAGINATION CONTROLS */}
    {totalPages > 1 && (
      <div className="flex justify-center items-center gap-3 mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className={`px-4 py-2 rounded-lg ${btn} disabled:opacity-40`}
        >
          Prev
        </button>

        <span className="text-sm font-semibold">
          Page {page} of {totalPages}
        </span>

        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className={`px-4 py-2 rounded-lg ${btn} disabled:opacity-40`}
        >
          Next
        </button>
      </div>
    )}
  </>
)}
        </div>
    </div>
    );
}
